import { useState, useEffect } from "react";
import {
  ShieldCheck,
  CheckCircle,
  AlertCircle,
  XCircle,
  Check,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { problemTextToPreview } from "./problem-text";
import { statusColor } from "./types";
import { qbFetch } from "./qb-cache";

interface GeneratedProblemAPI {
  id: string;
  skill_id?: string;
  standard_code?: string;
  question_text?: string;
  difficulty_level?: string;
  status?: string; // "approved" | "rejected" | "pending_review" | "pending"
  eval_math_correct?: boolean;
  eval_standard_aligned?: boolean;
  eval_difficulty_appropriate?: boolean;
  run_id?: string;
}

interface GenerationRun {
  id: string;
  status: string;
  total_generated?: number;
  total_approved?: number;
  total_rejected?: number;
  total_pending_review?: number;
  created_at?: string;
}

function mapStatusToVerdict(status?: string): "pass" | "fail" | "review" {
  if (status === "approved") return "pass";
  if (status === "rejected") return "fail";
  return "review";
}

function StatCard({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: React.ElementType;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-1 p-3.5">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {label}
          </span>
          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
        <div className="text-2xl font-bold tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}

function EvalBadge({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold ${
        ok
          ? "bg-emerald-50 text-emerald-700"
          : "bg-red-50 text-red-700"
      }`}
    >
      {ok ? <Check className="h-2.5 w-2.5" /> : <X className="h-2.5 w-2.5" />} {label}
    </span>
  );
}

export function EvaluatorSystem() {
  const [problems, setProblems] = useState<GeneratedProblemAPI[]>([]);
  const [runs, setRuns] = useState<GenerationRun[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadEvaluatorData() {
      setLoading(true);
      const [problemsData, runsData] = await Promise.all([
        qbFetch<{ items: GeneratedProblemAPI[] } | GeneratedProblemAPI[]>("/api/proxy/question-bank/generated-problems?limit=20")
          .then((data) => Array.isArray(data) ? data : (data.items || []))
          .catch(() => [] as GeneratedProblemAPI[]),
        qbFetch<{ items: GenerationRun[] } | GenerationRun[]>("/api/proxy/question-bank/generation-runs?limit=5")
          .then((data) => Array.isArray(data) ? data : (data.items || []))
          .catch(() => [] as GenerationRun[]),
      ]);
      if (cancelled) return;
      setProblems(problemsData);
      setRuns(runsData);
      setLoading(false);
    }

    void loadEvaluatorData();
    return () => {
      cancelled = true;
    };
  }, []);

  // Compute stats from actual data
  const totalEvaluated = problems.length;
  const passedCount = problems.filter((p) => p.status === "approved").length;
  const reviewCount = problems.filter((p) => p.status === "pending_review" || p.status === "pending").length;
  const rejectedCount = problems.filter((p) => p.status === "rejected").length;

  // If runs provide aggregate stats, use those for the stat cards (they may be more accurate)
  const runTotals = runs.reduce(
    (acc, r) => ({
      generated: acc.generated + (r.total_generated || 0),
      approved: acc.approved + (r.total_approved || 0),
      rejected: acc.rejected + (r.total_rejected || 0),
      review: acc.review + (r.total_pending_review || 0),
    }),
    { generated: 0, approved: 0, rejected: 0, review: 0 },
  );

  const hasRunStats = runTotals.generated > 0;
  const statsEvaluated = hasRunStats ? runTotals.generated : totalEvaluated;
  const statsPassed = hasRunStats ? runTotals.approved : passedCount;
  const statsReview = hasRunStats ? runTotals.review : reviewCount;
  const statsRejected = hasRunStats ? runTotals.rejected : rejectedCount;

  // Find the first rejected problem for the failure detail card
  const failedProblem = problems.find((p) => p.status === "rejected" && p.eval_math_correct === false);

  if (loading) {
    return (
      <div className="space-y-5">
        <div>
          <h2 className="text-xl font-bold">Evaluator System</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">Automated quality gate</p>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="flex flex-col gap-1 p-3.5">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-3.5 w-3.5 rounded-sm" />
                </div>
                <Skeleton className="h-7 w-12" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <div className="border-b px-4 py-3 text-sm font-semibold">Recent Evaluations</div>
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="flex items-center justify-between border-b px-4 py-3 last:border-b-0"
            >
              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-2 w-2 rounded-full" />
                  <Skeleton className="h-3.5 w-24" />
                  <Skeleton className="h-3.5 w-16" />
                </div>
                <Skeleton className="h-3 w-3/4" />
              </div>
              <div className="ml-4 flex shrink-0 items-center gap-1.5">
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-4 w-10" />
                <Skeleton className="h-4 w-12" />
              </div>
            </div>
          ))}
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold">Evaluator System</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">Automated quality gate</p>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <StatCard label="Evaluated" value={String(statsEvaluated)} icon={ShieldCheck} />
        <StatCard label="Passed" value={String(statsPassed)} icon={CheckCircle} />
        <StatCard label="Review" value={String(statsReview)} icon={AlertCircle} />
        <StatCard label="Rejected" value={String(statsRejected)} icon={XCircle} />
      </div>

      <Card>
        <div className="border-b px-4 py-3 text-sm font-semibold">
          Recent Evaluations
        </div>
        {problems.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No generated problems found
          </div>
        ) : (
          problems.map((p) => {
            const verdict = mapStatusToVerdict(p.status);
            const mathOk = p.eval_math_correct ?? true;
            const stdOk = p.eval_standard_aligned ?? true;
            const diffOk = p.eval_difficulty_appropriate ?? true;
            return (
              <div
                key={p.id}
                className="flex items-center justify-between border-b px-4 py-3 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <span
                      className={`inline-block h-2 w-2 rounded-full ${statusColor(verdict)}`}
                    />
                    <span className="text-sm font-semibold">{p.id.slice(0, 12)}</span>
                    {p.standard_code && (
                      <Badge variant="outline" className="text-[10px]">
                        {p.standard_code}
                      </Badge>
                    )}
                  </div>
                  <p className="ml-4 truncate text-xs text-muted-foreground">
                    {problemTextToPreview(p.question_text) || "No question text"}
                  </p>
                </div>
                <div className="ml-4 flex shrink-0 items-center gap-1.5">
                  <EvalBadge ok={mathOk} label="Math" />
                  <EvalBadge ok={stdOk} label="Std" />
                  <EvalBadge ok={diffOk} label="Diff" />
                </div>
              </div>
            );
          })
        )}
      </Card>

      {/* Failure Detail — show first math-failed problem if any */}
      {failedProblem && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="mb-2 flex items-center gap-2">
              <XCircle className="h-4 w-4 text-red-500" />
              <span className="text-sm font-bold text-red-800">
                {failedProblem.id.slice(0, 12)} — Math Verification Failed
              </span>
            </div>
            <p className="mb-2 text-sm text-red-700">
              {problemTextToPreview(failedProblem.question_text) || "No question text"}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
